import { inject, injectable } from 'inversify'
import {
  CreateCompanyArticleUseCaseInput,
  CreateCompanyArticleUseCaseOutput,
  IAppErrorFactory,
  IArticlesService,
  ICompanyArticleFactory,
  ICreateCompanyArticleUseCase,
} from '@/types'
import symbols from '@/symbols'
import { filterBase64 } from '@/utils'
import { ClientError } from 'graphql-request'

@injectable()
export default class CreateCompanyArticleInteractor implements ICreateCompanyArticleUseCase {
  @inject(symbols.IArticlesService) private articlesService: IArticlesService

  @inject(symbols.ICompanyArticleFactory) private companyArticleFactory: ICompanyArticleFactory

  @inject(symbols.IAppErrorFactory) private errorFactory: IAppErrorFactory

  async handle(input: CreateCompanyArticleUseCaseInput): Promise<CreateCompanyArticleUseCaseOutput> {
    const output = this.getDefaultOutputData()
    try {
      const base = await this.articlesService.createCompanyArticle(input)
      output.article = this.companyArticleFactory.create({ base })
    } catch (e) {
      const filteredInput = { ...input }
      filteredInput.article.thumbnail = filterBase64(input.article.thumbnail)
      filteredInput.article.ogImage = filterBase64(input.article.ogImage)

      const error = e as ClientError
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const errorExtension = error?.response?.errors[0]?.extensions?.code

      let message = '記事の作成に失敗しました'
      if (errorExtension === 'DAILY_ARTICLE_LIMIT_ERROR') {
        message = '一日一記事しか作成できません。'
      }

      output.error = this.errorFactory.create({
        originalInstance: e as Error,
        messageForUI: message,
        extra: {
          useCaseInput: filteredInput,
        },
      })
    }

    return output
  }

  private getDefaultOutputData(): CreateCompanyArticleUseCaseOutput {
    return {
      article: null,
      error: null,
    }
  }
}
